
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { UTILITIES } from "@/store/passenger-store";

@Component({ name: "ConfirmChangeRequestDialog" })
export default class ConfirmChangeRequestDialog extends Vue {
  @Prop({ required: true })
  value!: boolean;

  @Prop({ default: null })
  changeRequest!: Partial<IPassengerChangeRequest> | null;

  @Prop({ default: false })
  isCancellation!: boolean;

  private focusTrapHandler: ((e: KeyboardEvent) => void) | null = null;
  private hiddenElements: HTMLElement[] = [];

  get title(): string {
    return this.isCancellation
      ? this.$t("passenger.cancel_change_request_title") as string
      : this.$t("passenger.confirm_change_request_title") as string;
  }

  get text(): string {
    return this.isCancellation
      ? this.$t("passenger.cancel_change_request_text") as string
      : this.$t("passenger.confirm_change_request_text") as string;
  }

  get utilityLines(): string[] {
    if (!this.changeRequest) return [];
    const lines: string[] = [];
    this.parseIds(this.changeRequest.removedUtilities).forEach(id => {
      const key = UTILITIES.find(u => u.id === id)?.key;
      if (key) lines.push(`${this.$t("utility." + key)} ${this.$t("utility.will_be_removed")}`);
    });
    this.parseIds(this.changeRequest.addedUtilities).forEach(id => {
      const key = UTILITIES.find(u => u.id === id)?.key;
      if (key) lines.push(`${this.$t("utility." + key)} ${this.$t("utility.will_be_added")}`);
    });
    return lines;
  }

  private parseIds(str: string | undefined): number[] {
    if (!str) return [];
    return str.split(",").map(s => Number.parseInt(s.trim(), 10)).filter(n => !Number.isNaN(n));
  }

  @Watch("value")
  onValueChanged(val: boolean) {
    if (val) {
      setTimeout(() => {
        const backBtn = (this.$refs.backBtn as Vue)?.$el as HTMLElement;
        const confirmBtn = (this.$refs.confirmBtn as Vue)?.$el as HTMLElement;

        const dialogWrapper = document.querySelector<HTMLElement>('.confirm-dialog-content');

        if (dialogWrapper) {
          dialogWrapper.setAttribute('role', 'dialog');
          dialogWrapper.setAttribute('aria-modal', 'true');
          dialogWrapper.setAttribute('aria-labelledby', 'confirm-dialog-title');

          const innerDialog = dialogWrapper.querySelector<HTMLElement>('[role="dialog"]');
          if (innerDialog) {
            innerDialog.setAttribute('aria-modal', 'true');
            innerDialog.setAttribute('aria-labelledby', 'confirm-dialog-title');
          }

          let el: HTMLElement | null = dialogWrapper;
          while (el && el !== document.body) {
            const parent: HTMLElement | null = el.parentElement;
            if (parent) {
              Array.from(parent.children).forEach(child => {
                if (child !== el) {
                  (child as HTMLElement).setAttribute('aria-hidden', 'true');
                  (child as HTMLElement).setAttribute('inert', '');
                  this.hiddenElements.push(child as HTMLElement);
                }
              });
            }
            el = parent;
          }
        }

        backBtn?.focus();

        this.focusTrapHandler = (e: KeyboardEvent) => {
          if (e.key !== 'Tab') return;
          const active = document.activeElement;
          if (e.shiftKey) {
            if (backBtn?.contains(active)) {
              e.preventDefault();
              confirmBtn?.focus();
            }
          } else if (confirmBtn?.contains(active)) {
            e.preventDefault();
            backBtn?.focus();
          }
        };

        document.addEventListener('keydown', this.focusTrapHandler);
      }, 300);
    } else {
      this.hiddenElements.forEach(el => {
        el.removeAttribute('aria-hidden');
        el.removeAttribute('inert');
      });
      this.hiddenElements = [];

      if (this.focusTrapHandler) {
        document.removeEventListener('keydown', this.focusTrapHandler);
        this.focusTrapHandler = null;
      }
    }
  }

  onConfirm() {
    this.$emit("input", false);
    this.$emit("confirm");
  }

  beforeDestroy() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }
}
